// AZ IDŐSÁV GEOMETRIÁJA — tiszta függvények, DOM nélkül.
//
// MIÉRT KÜLÖN FÁJL: a sáv helye és hossza a nézet EGYETLEN nem-triviális számítása (ablak, vágás, ma-vonal),
// és pont az a fajta kód, ami csendben romlik el (egy nappal elcsúszó sáv senkinek nem tűnik fel). Tiszta
// függvényként DOM-mock nélkül, pontosan tesztelhető.
//
// MINDEN DÁTUM NAP-PONTOS (`YYYY-MM-DD`) ÉS UTC-BEN SZÁMOLT. Az UTC itt technikai reprezentáció: a naptári
// napok közti TÁVOLSÁGOT számoljuk, nem időpontot ábrázolunk — helyi zónában a nyári/téli váltás egy 23 vagy
// 25 órás napot adna, és a sáv fél nappal elcsúszna.

export type TimelineZoom = 'month' | 'quarter' | 'year';

export interface TimelineWindow {
  from: string;
  to: string;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toUtc(day: string): number {
  return Date.parse(`${day}T00:00:00.000Z`);
}

function fromUtc(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Két nap közti TELJES napok száma (előjeles). */
export function daysBetween(a: string, b: string): number {
  return Math.round((toUtc(b) - toUtc(a)) / MS_PER_DAY);
}

/** Egy nap eltolása n nappal. */
export function addDays(day: string, n: number): string {
  return fromUtc(toUtc(day) + n * MS_PER_DAY);
}

/** Az adott hónap első napja (`YYYY-MM-01`). */
function monthStart(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

/** Az adott hónap ELSŐ napjától n hónappal odébb lévő hónap első napja. */
export function addMonths(day: string, n: number): string {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7)) - 1 + n;
  const y = year + Math.floor(month / 12);
  const m = ((month % 12) + 12) % 12;
  return `${String(y).padStart(4, '0')}-${String(m + 1).padStart(2, '0')}-01`;
}

/**
 * A látható ABLAK egy horgony-napból és a nagyításból. A horgony bárhol lehet az időszakon belül — az ablak
 * mindig NAPTÁRI határokra igazodik (hónap/negyedév/év eleje–vége), hogy a tengely feliratai stabilak
 * legyenek, és a „következő időszak" lépés ne csússzon el fokozatosan.
 */
export function windowFor(zoom: TimelineZoom, anchor: string): TimelineWindow {
  if (zoom === 'year') {
    const year = anchor.slice(0, 4);
    return { from: `${year}-01-01`, to: `${year}-12-31` };
  }
  if (zoom === 'quarter') {
    const month = Number(anchor.slice(5, 7));
    const firstMonth = Math.floor((month - 1) / 3) * 3 + 1;
    const from = `${anchor.slice(0, 4)}-${String(firstMonth).padStart(2, '0')}-01`;
    return { from, to: addDays(addMonths(from, 3), -1) };
  }
  const from = monthStart(anchor);
  return { from, to: addDays(addMonths(from, 1), -1) };
}

/**
 * Az AKTUÁLIS időszak ember-olvasható felirata a navigáció-sor közepére — `2026. szeptember` ·
 * `2026. III. negyedév` · `2026`.
 *
 * MIÉRT KELL: a `‹ › ma` gombok önmagukban VAKON léptetnek — a lapon eddig sehol nem látszott, melyik
 * időszakot nézed (az idősávon csak tengely-osztások vannak). Ez a felirat az, ami a navigációt
 * értelmezhetővé teszi; a megbeszélés-naptár ugyanezt a slotot használja a saját címére.
 *
 * A hónap-nevet az `Intl` adja a felület nyelvén (nincs hardcode-olt hónap-lista); a negyedév római száma
 * nyelv-független, a köré kerülő szöveget a hívó i18n-kulcsból adja.
 */
export function periodLabel(
  zoom: TimelineZoom,
  anchor: string,
  locale: string,
  quarterWord: string,
): string {
  const { from } = windowFor(zoom, anchor);
  const year = from.slice(0, 4);
  if (zoom === 'year') return year;
  if (zoom === 'quarter') {
    const q = Math.floor(Number(from.slice(5, 7)) / 3) + 1;
    return `${year}. ${['I', 'II', 'III', 'IV'][q - 1] ?? String(q)}. ${quarterWord}`;
  }
  const month = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(
    new Date(`${from}T00:00:00Z`),
  );
  return `${year}. ${month}`;
}

/** Lépés előre/hátra egy teljes időszakot (a horgony az ÚJ ablak első napja lesz). */
export function shiftAnchor(zoom: TimelineZoom, anchor: string, direction: 1 | -1): string {
  const step = zoom === 'year' ? 12 : zoom === 'quarter' ? 3 : 1;
  return addMonths(windowFor(zoom, anchor).from, step * direction);
}

export interface TimelineTick {
  /** A tick NAPJA — a feliratot a komponens formázza (nyelv-függő hónapnév). */
  date: string;
  /** Bal pozíció az ablakon belül, százalékban. */
  offsetPct: number;
  /** `true` → hónap-határ (vastagabb vonal); `false` → belső (heti) osztás. */
  major: boolean;
}

/**
 * A tengely osztásai. Hónap-nagyításban HETI osztás (a hónapon belül a hét a tájékozódási egység),
 * negyedév/év nagyításban HÓNAP-határ. Az ablak első napja mindig tick — a záró él nem, mert az a
 * következő időszak első napja lenne.
 */
export function axisTicks(window: TimelineWindow, zoom: TimelineZoom): TimelineTick[] {
  const total = daysBetween(window.from, window.to) + 1;
  const ticks: TimelineTick[] = [];
  if (zoom === 'month') {
    for (let offset = 0; offset < total; offset += 7) {
      ticks.push({
        date: addDays(window.from, offset),
        offsetPct: (offset / total) * 100,
        major: offset === 0,
      });
    }
    return ticks;
  }
  let cursor = window.from;
  while (cursor <= window.to) {
    const offset = daysBetween(window.from, cursor);
    ticks.push({ date: cursor, offsetPct: (offset / total) * 100, major: true });
    cursor = addMonths(cursor, 1);
  }
  return ticks;
}

export interface BarGeometry {
  leftPct: number;
  widthPct: number;
  /** `true` → a kampány az ablak ELŐTT kezdődött (a sáv bal éle vágott, a felület ezt jelzi). */
  clippedStart: boolean;
  /** `true` → a kampány az ablak UTÁN ér véget. */
  clippedEnd: boolean;
}

/**
 * Egy kampány sávja a látható ablakban. `null` → a kampány nem lóg bele (nem rajzolunk).
 *
 * A SZÉLESSÉG ZÁRT intervallumon számol: egy egynapos kampány is LÁTHATÓ sávot kap (1 nap / ablak-hossz),
 * nem nulla szélességűt — ez a leggyakoribb geometriai hiba az ilyen nézetekben.
 */
export function barGeometry(
  startsOn: string,
  endsOn: string,
  window: TimelineWindow,
): BarGeometry | null {
  if (startsOn > window.to || endsOn < window.from) return null;
  const total = daysBetween(window.from, window.to) + 1;
  const start = startsOn < window.from ? window.from : startsOn;
  const end = endsOn > window.to ? window.to : endsOn;
  const leftDays = daysBetween(window.from, start);
  const spanDays = daysBetween(start, end) + 1;
  return {
    leftPct: (leftDays / total) * 100,
    widthPct: (spanDays / total) * 100,
    clippedStart: startsOn < window.from,
    clippedEnd: endsOn > window.to,
  };
}

/** A MA-vonal pozíciója az ablakban, vagy `null`, ha a mai nap kívül esik. */
export function todayOffsetPct(window: TimelineWindow, today: string): number | null {
  if (today < window.from || today > window.to) return null;
  const total = daysBetween(window.from, window.to) + 1;
  // A nap KÖZEPÉRE tesszük: a vonal így a mai nap sávjának közepén áll, nem az előző nap határán.
  return ((daysBetween(window.from, today) + 0.5) / total) * 100;
}

export interface TimelineGroup<T> {
  accountId: string;
  accountName: string | null;
  rows: T[];
}

/**
 * Fiókonkénti csoportosítás, a fiók-nevek szerint rendezve (a névtelen/feloldhatatlan fiók a végére). A
 * csoporton belül a kampányok kezdés szerint állnak — ez az idősáv olvasási iránya.
 */
export function groupByAccount<
  T extends { accountId: string; accountName: string | null; startsOn: string },
>(rows: readonly T[], locale = 'hu'): TimelineGroup<T>[] {
  const byAccount = new Map<string, TimelineGroup<T>>();
  for (const row of rows) {
    const group = byAccount.get(row.accountId);
    if (group) group.rows.push(row);
    else
      byAccount.set(row.accountId, {
        accountId: row.accountId,
        accountName: row.accountName,
        rows: [row],
      });
  }
  const collator = new Intl.Collator(locale);
  const groups = [...byAccount.values()];
  groups.sort((a, b) => {
    if (a.accountName === null && b.accountName === null) return 0;
    if (a.accountName === null) return 1;
    if (b.accountName === null) return -1;
    return collator.compare(a.accountName, b.accountName);
  });
  for (const group of groups) group.rows.sort((a, b) => a.startsOn.localeCompare(b.startsOn));
  return groups;
}
